// Shared definition of the logo-reproduction wizard for the step sidebar.
const REPRODUCTION_FLOW_STEPS = ["Set up", "Edit colors", "Marking"];

export function reproductionSidebar(currentIndex, componentCount = null) {
    return {
        steps: REPRODUCTION_FLOW_STEPS.map((label) => ({ key: label, label })),
        currentIndex,
        iterationValue: componentCount,
        iterationLabel: "LOGO REPRODUCTION",
        valueCaption: "Components",
    };
}

// Shared definition of the image-reproduction wizard: no color-editing step,
// the halftoning assigns the palette colors itself. The gamut-mapping step
// compresses the image into the palette's gamut before the separation.
const IMAGE_REPRODUCTION_FLOW_STEPS = ["Set up", "Gamut mapping", "Marking"];

export function imageReproductionSidebar(currentIndex) {
    return {
        steps: IMAGE_REPRODUCTION_FLOW_STEPS.map((label) => ({ key: label, label })),
        currentIndex,
        iterationValue: null,
        iterationLabel: "IMAGE REPRODUCTION",
        valueCaption: null,
    };
}

// Shared definition of the gradient-reproduction wizard: the middle step
// picks the ordered palette colors the blend runs through and its direction.
export const GRADIENT_REPRODUCTION_FLOW_STEPS = ["Set up", "Gradient", "Marking"];

export function gradientReproductionSidebar(currentIndex, colorCount = null) {
    return {
        steps: GRADIENT_REPRODUCTION_FLOW_STEPS.map((label) => ({ key: label, label })),
        currentIndex,
        iterationValue: colorCount,
        iterationLabel: "GRADIENT REPRODUCTION",
        valueCaption: colorCount === null ? null : "Colors",
    };
}

export const GRADIENT_DIRECTIONS = [
    { value: "horizontal", label: "Horizontal (left to right)" },
    { value: "vertical", label: "Vertical (top to bottom)" },
];

// What one blend spans: the whole design once, or every shape (SVG element /
// connected raster area) on its own.
export const GRADIENT_SCOPES = [
    { value: "whole", label: "Across the whole design" },
    { value: "shape", label: "Each shape on its own" },
];

export const gradientScopeLabel = (value) => (
    GRADIENT_SCOPES.find((option) => option.value === value)?.label || value || "\u2014"
);

const IMAGE_UPLOAD_EXTENSIONS = [".png", ".jpg", ".jpeg"];
const GRADIENT_UPLOAD_EXTENSIONS = [".svg", ".png", ".jpg", ".jpeg"];

/**
 * The three reproduction kinds, keyed by their URL segment: the list page is
 * /colorreproductions/<kind>, the create page /colorreproductions/<kind>/new.
 * Per kind: the reproduction_type value, the wording of the list and create
 * pages, the accepted upload files, and the create endpoint + form field.
 */
export const REPRODUCTION_KINDS = {
    logo: {
        type: "LOGO_REPRODUCTION",
        listTitle: "Logo reproductions",
        navLabel: "Logo",
        newTitle: "New logo reproduction",
        newHint: "Name the reproduction and upload the logo as an SVG file.",
        chooseLabel: "Choose SVG file",
        previewAlt: "Logo preview",
        previewHint: "Logo preview appears here",
        emptyHint: "No logo reproductions yet — create the first one.",
        emptyHintMine: "You have no logo reproductions yet — create one.",
        fileLabel: "Logo file",
        extensions: [".svg"],
        extensionError: "Please choose an SVG file (.svg).",
        endpoint: "api/reproduction/add_reproduction",
        field: "svg_file",
    },
    image: {
        type: "IMAGE_REPRODUCTION",
        listTitle: "Image reproductions",
        navLabel: "Image",
        newTitle: "New image reproduction",
        newHint: "Name the reproduction and upload the image as a PNG or JPEG file.",
        chooseLabel: "Choose image file",
        previewAlt: "Image preview",
        previewHint: "Image preview appears here",
        emptyHint: "No image reproductions yet — create the first one.",
        emptyHintMine: "You have no image reproductions yet — create one.",
        fileLabel: "Image file",
        extensions: IMAGE_UPLOAD_EXTENSIONS,
        extensionError: "Please choose a PNG or JPEG image (.png, .jpg, .jpeg).",
        endpoint: "api/reproduction/add_image_reproduction",
        field: "image_file",
    },
    gradient: {
        type: "GRADIENT_REPRODUCTION",
        listTitle: "Gradient reproductions",
        navLabel: "Gradient",
        newTitle: "New gradient reproduction",
        newHint: "Name the reproduction and upload the shape to fill: an SVG, or a PNG/JPEG whose transparent pixels stay unmarked.",
        chooseLabel: "Choose shape file",
        previewAlt: "Shape preview",
        previewHint: "Shape preview appears here",
        emptyHint: "No gradient reproductions yet — create the first one.",
        emptyHintMine: "You have no gradient reproductions yet — create one.",
        fileLabel: "Shape file",
        extensions: GRADIENT_UPLOAD_EXTENSIONS,
        extensionError: "Please choose an SVG, PNG or JPEG file (.svg, .png, .jpg, .jpeg).",
        endpoint: "api/reproduction/add_gradient_reproduction",
        field: "shape_file",
    },
};

export const REPRODUCTION_KIND_SLUGS = Object.keys(REPRODUCTION_KINDS);

/** URL segment of a reproduction_type value (unknown/empty -> logo). */
export function reproductionKindSlug(type) {
    const normalized = (type || "").toUpperCase();
    const found = REPRODUCTION_KIND_SLUGS.find(
        (slug) => REPRODUCTION_KINDS[slug].type === normalized,
    );
    return found || "logo";
}

/** The list page of the reproductions of the given type. */
export const REPRODUCTIONS_BASE = "/colorreproductions";
/** The page a new reproduction starts on: pick the kind, then upload. */
export const REPRODUCTION_TYPE_CHOICE_ROUTE = "/colorreproductiontype";
export function reproductionListRoute(type) {
    return `${REPRODUCTIONS_BASE}/${reproductionKindSlug(type)}`;
}

/** The create page for a reproduction of the given type. */
export function newReproductionRoute(type) {
    return `${reproductionListRoute(type)}/new`;
}

/** The steps a reproduction page can be at (URL segments): the shared set
 *  up and marking steps, and the colour assignment step of each kind. */
export const REPRODUCTION_STEPS = ["setup", "edit", "gamut", "gradient", "marking"];

/** A page of one reproduction: /colorreproductions/<kind>/<id>/<step>. The
 *  id in the URL is what keeps two browser tabs on two reproductions apart. */
export function reproductionRoute(type, reproductionId, step) {
    return `${reproductionListRoute(type)}/${reproductionId}/${step}`;
}

/** Short human label for a reproduction_type value. */
export function reproductionTypeLabel(type) {
    switch ((type || "").toUpperCase()) {
        case "IMAGE_REPRODUCTION":
            return "Image";
        case "GRADIENT_REPRODUCTION":
            return "Gradient";
        case "LOGO_REPRODUCTION":
            return "Logo";
        default:
            return "Logo";
    }
}

/** The step sidebar for a reproduction type (index = current step). */
export function sidebarForType(type, currentIndex, count = null) {
    switch ((type || "").toUpperCase()) {
        case "IMAGE_REPRODUCTION":
            return imageReproductionSidebar(currentIndex);
        case "GRADIENT_REPRODUCTION":
            return gradientReproductionSidebar(currentIndex, count);
        default:
            return reproductionSidebar(currentIndex, count);
    }
}

/** The step on which a reproduction's colors are assigned: where a copy
 * of a final reproduction opens so a few assignments can be redone. */
export function assignmentStep(type) {
    switch ((type || "").toUpperCase()) {
        case "IMAGE_REPRODUCTION":
            return { step: "gamut", label: "Gamut mapping" };
        case "GRADIENT_REPRODUCTION":
            return { step: "gradient", label: "Gradient" };
        default:
            return { step: "edit", label: "Edit colors" };
    }
}

/** Name suggested for a copy: "<name> v2", and "<name> v2" -> "<name> v3". */
export function suggestedCopyName(name) {
    const base = (name || "").trim();
    const versioned = /^(.*\S)\s+v(\d+)$/.exec(base);
    if (versioned) return `${versioned[1]} v${Number(versioned[2]) + 1}`;
    return `${base || "Reproduction"} v2`;
}

/** Status presentation for reproduction cards and marking runs: friendly
 * label + MUI chip color. A reproduction without any run reads as "Not
 * computed"; a run is COMPUTING, FINISHED or FAILED. */
export function reproductionStatusPresentation(status) {
    const normalized = (status || "NOT_COMPUTED").toUpperCase();
    switch (normalized) {
        case "NOT_COMPUTED":
            return { label: "Not computed", color: "default" };
        case "COMPUTING":
            return { label: "Computing", color: "warning" };
        case "FINISHED":
            return { label: "Marking file ready", color: "success" };
        case "FAILED":
            return { label: "Failed", color: "error" };
        default:
            return {
                label: normalized
                    ? normalized.toLowerCase().split("_").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ")
                    : "Unknown",
                color: "default",
            };
    }
}

export const REPRODUCTION_MODES = [
    {
        value: "SHAPE_FILL_CLUSTERS",
        label: "Shape fill",
        description: "Each shape is filled individually with scanlines and marked as clusters.",
    },
    {
        value: "LINE_CLUSTERS",
        label: "Line clusters",
        description: "Every SVG path is marked as a cluster of parallel lines along it.",
    },
    {
        value: "RASTER_SCANLINES",
        label: "Rasterized scanlines",
        description: "Objects of the same color are merged into one layer, rasterized and marked scanline by scanline.",
    },
];

export function reproductionModeLabel(mode) {
    const found = REPRODUCTION_MODES.find((entry) => entry.value === mode);
    return found ? found.label : (mode || "—");
}

/** A logo's parsed components as a short label: color layers in the
 * rasterized scanline mode, shapes and lines otherwise. */
export function logoComponentsLabel(details) {
    const layers = details?.layer_count ?? 0;
    if (layers) return `${layers} color layer${layers === 1 ? "" : "s"}`;
    return `${details?.shape_count ?? 0} shapes · ${details?.line_count ?? 0} lines`;
}

/** Number of a logo's parsed components (shapes, lines and color layers). */
export function logoComponentCount(details) {
    return (details?.shape_count ?? 0) + (details?.line_count ?? 0) + (details?.layer_count ?? 0);
}

const mm = (value) => (value === null || value === undefined || value === "" ? null : `${value} mm`);

// Where a run was placed: centered on the reachable area (also runs from
// before their type had a position) or on the whole substrate, or the
// top-left corner actually used.
const positionChip = (run) => {
    if (run.position_mode === "center_substrate") {
        return { key: "position", label: "Position: centered on the substrate" };
    }
    if (run.position_mode !== "custom") return { key: "position", label: "Position: centered" };
    const left = run.placed_left_mm ?? run.position_x_mm;
    const top = run.placed_top_mm ?? run.position_y_mm;
    return { key: "position", label: `Position: left ${left} mm, top ${top} mm` };
};

// Where the substrate lay for a run; only worth a chip once it was moved.
const substrateChip = (run) => {
    const x = run.plate_offset_x_mm || 0;
    const y = run.plate_offset_y_mm || 0;
    if (!x && !y) return null;
    return { key: "substrate", label: `Substrate at x ${x} mm, y ${y} mm` };
};

/** The parameters of a marking run as chip labels for the history table:
 * ``[{ key, label }]``. ``modeLabel`` maps a logo reproduction mode to its
 * label. Same presentation as the sample-point details (outlined chips). */
export function markingParameterChips(marking, modeLabel = (mode) => mode) {
    const parameters = marking?.parameters || {};
    const chips = [];
    if (parameters.logo) {
        const logo = parameters.logo;
        chips.push({ key: "mode", label: `Mode: ${modeLabel(logo.reproduction_mode)}` });
        chips.push({ key: "height", label: `Height: ${mm(logo.marking_height_mm) || "fills the field"}` });
        chips.push({ key: "whitespace", label: `Whitespace: ${logo.trim_content_whitespace ? "pruned" : "whole canvas"}` });
        chips.push(positionChip(logo));
        if (substrateChip(logo)) chips.push(substrateChip(logo));
        return chips;
    }
    if (parameters.image) {
        const image = parameters.image;
        const requested = [mm(image.target_width_mm), mm(image.target_height_mm)].filter(Boolean).join(" x ");
        const marked = [mm(image.image_width_mm), mm(image.image_height_mm)].filter(Boolean).join(" x ");
        chips.push({ key: "size", label: `Size: ${marked || requested || "default"}` });
        chips.push(positionChip(image));
        if (substrateChip(image)) chips.push(substrateChip(image));
        chips.push({ key: "iterations", label: `Iterations: ${image.iterations}` });
        chips.push({ key: "gamut", label: `Gamut mapping: ${image.gamut_mapping_mode}` });
        if (image.cell_width_mm) chips.push({ key: "cell", label: `Cell: ${Math.round(image.cell_width_mm * 1000)} \u00b5m` });
        return chips;
    }
    if (parameters.gradient) {
        const gradient = parameters.gradient;
        chips.push({ key: "direction", label: `Direction: ${gradient.direction}` });
        if (gradient.scope) chips.push({ key: "scope", label: `Spans: ${gradient.scope === "shape" ? "each shape" : "whole design"}` });
        const requested = [mm(gradient.target_width_mm), mm(gradient.target_height_mm)].filter(Boolean).join(" x ");
        const marked = [mm(gradient.shape_width_mm), mm(gradient.shape_height_mm)].filter(Boolean).join(" x ");
        chips.push({ key: "size", label: `Size: ${marked || requested || "default"}` });
        chips.push(positionChip(gradient));
        if (substrateChip(gradient)) chips.push(substrateChip(gradient));
        if (marking.group_count) chips.push({ key: "colors", label: `Colors: ${marking.group_count}` });
        if (gradient.cell_width_mm) chips.push({ key: "cell", label: `Cell: ${Math.round(gradient.cell_width_mm * 1000)} \u00b5m` });
        return chips;
    }
    return chips;
}
